import {
  GUILD_EVENT_RSVP_STATES,
  GUILD_EVENT_STATUSES,
  type GuildEventRsvpState,
  type GuildEventScheduleTimezone,
  type GuildEventStatus,
  type WwmEventRoleKey,
} from "@/core/guild-events/types"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"

type NonEmptyArray<T> = [T, ...T[]]

type RaidEventFields = {
  eventKind: "raid"
  eventRoles: NonEmptyArray<WwmEventRoleKey>
  signupCloseAt: string
}

type AttendanceEventFields = {
  eventKind: "attendance"
  eventRoles: []
  signupCloseAt: null
}

type GuildEventKindFields = RaidEventFields | AttendanceEventFields

type GuildEventRsvpFields = {
  rsvpEnabled: boolean
}

type GuildEventRecordBase = {
  id: string
  gameKey: "where-winds-meet"
  channelId: string
  discordMessageId: string | null
  title: string
  description: string
  startAt: string
  status: GuildEventStatus
  syncStatus: "healthy" | "degraded"
  lastSyncError: string | null
  signupCloseJobId: string | null
  startNotifyJobId: string | null
  scheduleId: string | null
  updatedBy: string
  updatedByDisplayName?: string | null
  channelName?: string | null
  memberRsvpState?: GuildEventRsvpState | null
  createdAt: string
  updatedAt: string
}

export type GuildEventRecord = GuildEventRecordBase &
  GuildEventRsvpFields &
  GuildEventKindFields

export type GuildEventSignupRecord = {
  id: string
  eventId: string
  memberId: string
  state: GuildEventRsvpState
  roleKey: WwmEventRoleKey | null
  updatedBy: string
  createdAt: string
  updatedAt: string
  guildDisplayName: string
}

export type GuildEventDetailRecord = {
  event: GuildEventRecord
  signups: GuildEventSignupRecord[]
}

type EventInputBase = {
  channelId?: string
  title: string
  description: string
  startAt: string
}

export type EventCreateInput = EventInputBase &
  GuildEventRsvpFields &
  GuildEventKindFields

export type EventUpdateInput =
  | {
      eventKind: "raid"
      title?: string
      description?: string
      eventRoles?: NonEmptyArray<WwmEventRoleKey>
      startAt?: string
      signupCloseAt?: string
      rsvpEnabled?: boolean
    }
  | {
      eventKind: "attendance"
      title?: string
      description?: string
      eventRoles?: []
      startAt?: string
      signupCloseAt?: null
      rsvpEnabled?: boolean
    }

export type EventDuplicateInput =
  | {
      eventKind: "raid"
      startAt: string
      signupCloseAt: string
    }
  | {
      eventKind: "attendance"
      startAt: string
    }

export type GuildEventScheduleWeekday =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"

export type GuildEventSchedule = {
  id: string
  title: string
  description: string
  eventKind: "raid" | "attendance"
  eventRoles: WwmEventRoleKey[]
  channelId: string
  weekdays: GuildEventScheduleWeekday[]
  timezone: GuildEventScheduleTimezone
  startTime: string
  signupCloseTime: string | null
  enabled: boolean
  rsvpEnabled: boolean
  createdAt: string
  updatedAt: string
}

export type GuildEventScheduleOccurrence = {
  occurrenceDate: string
  startAt: string
  signupCloseAt: string | null
}

export type GuildEventScheduleOverview = {
  schedule: GuildEventSchedule
  disabled: boolean
  todayMatches: boolean
  todayOccurrence: GuildEventScheduleOccurrence | null
  nextOccurrence: GuildEventScheduleOccurrence | null
  todayEvent: GuildEventRecord | null
  nextEvent: GuildEventRecord | null
}

export type GuildEventGenerationPreviewItem = {
  scheduleId: string
  title: string
  channelId: string
  startAt: string
  signupCloseAt: string | null
  willPostAnnouncement: true
  disposition: "will_create" | "already_generated"
}

export type GuildEventGenerationResult = {
  created: Array<{
    kind: "created"
    scheduleId: string
    event: GuildEventRecord
  }>
  skipped: Array<{
    kind: "skipped"
    scheduleId: string
    reason: "already_generated" | "not_matching"
  }>
  failed: Array<{
    kind: "failed"
    scheduleId: string
    error: string
    event: GuildEventRecord | null
    degraded: boolean
  }>
}

type ScheduleInputBase = {
  title: string
  description: string
  eventKind: "raid" | "attendance"
  eventRoles: WwmEventRoleKey[]
  channelId?: string
  weekdays: GuildEventScheduleWeekday[]
  startTime: string
  rsvpEnabled?: boolean
}

export type GuildEventScheduleCreateInput =
  | (ScheduleInputBase & {
      eventKind: "raid"
      signupCloseTime: string
      enabled?: boolean
    })
  | (ScheduleInputBase & {
      eventKind: "attendance"
      eventRoles: []
      signupCloseTime: null
      enabled?: boolean
    })

type ScheduleUpdateInputBase = Partial<
  Omit<ScheduleInputBase, "eventKind" | "eventRoles">
> & {
  enabled?: boolean
}

export type GuildEventScheduleUpdateInput =
  | (ScheduleUpdateInputBase & {
      eventKind?: "raid"
      eventRoles?: WwmEventRoleKey[]
      signupCloseTime?: string
    })
  | (ScheduleUpdateInputBase & {
      eventKind?: "attendance"
      eventRoles?: []
      signupCloseTime?: null
    })

type SignupMutationInput = {
  state: GuildEventRsvpState
  roleKey: WwmEventRoleKey | null
}

type MemberListFilters = {
  state?: "all" | GuildEventRsvpState
  bucket?: "all" | "upcoming" | "past" | "active" | "archived" | "cancelled"
  page?: number
  pageSize?: number
}

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as {
      error?: { message?: string }
      message?: string
    }

    return body.error?.message ?? body.message ?? fallback
  } catch {
    return fallback
  }
}

export class GuildEventsApiError extends Error {
  readonly code: string | null
  constructor(message: string, code: string | null) {
    super(message)
    this.name = "GuildEventsApiError"
    this.code = code
  }
}

const readApiErrorBody = async (
  response: Response,
  fallback: string,
): Promise<{ code: string | null; message: string }> => {
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string }
      message?: string
    }

    return {
      code: body.error?.code ?? null,
      message: body.error?.message ?? body.message ?? fallback,
    }
  } catch {
    return { code: null, message: fallback }
  }
}

const getCsrfToken = (): string => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) {
    throw new Error("Missing CSRF token")
  }

  return csrfToken
}

const assertState = (value: string): GuildEventRsvpState => {
  if (GUILD_EVENT_RSVP_STATES.includes(value as GuildEventRsvpState)) {
    return value as GuildEventRsvpState
  }

  throw new Error("Invalid RSVP state")
}

const assertStatus = (value: string): GuildEventStatus => {
  if (GUILD_EVENT_STATUSES.includes(value as GuildEventStatus)) {
    return value as GuildEventStatus
  }

  throw new Error("Invalid event status")
}

export const guildEventsApi = {
  listAdminEvents: async (filters?: {
    status?: string
    bucket?: string
    query?: string
    page?: number
    pageSize?: number
  }): Promise<{ items: GuildEventRecord[]; total: number }> => {
    const searchParams = new URLSearchParams()
    if (filters?.status && filters.status !== "all") {
      searchParams.set("status", filters.status)
    }
    if (filters?.bucket && filters.bucket !== "all") {
      searchParams.set("bucket", filters.bucket)
    }
    if (filters?.query) {
      searchParams.set("query", filters.query)
    }
    if (filters?.page) {
      searchParams.set("page", String(filters.page))
    }
    if (filters?.pageSize) {
      searchParams.set("pageSize", String(filters.pageSize))
    }

    const queryString = searchParams.toString()
    const url = queryString
      ? `/api/admin/guild-events?${queryString}`
      : "/api/admin/guild-events"

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load guild events"),
      )
    }

    const payload = (await response.json()) as {
      items: GuildEventRecord[]
      total: number
    }
    return payload
  },
  listMemberEvents: async (
    filters?: MemberListFilters,
  ): Promise<{ items: GuildEventRecord[]; total: number }> => {
    const searchParams = new URLSearchParams()
    if (filters?.state && filters.state !== "all") {
      searchParams.set("state", filters.state)
    }
    if (filters?.bucket && filters.bucket !== "all") {
      searchParams.set("bucket", filters.bucket)
    }
    if (filters?.page) {
      searchParams.set("page", String(filters.page))
    }
    if (filters?.pageSize) {
      searchParams.set("pageSize", String(filters.pageSize))
    }

    const queryString = searchParams.toString()
    const url =
      queryString.length > 0
        ? `/api/guild-events?${queryString}`
        : "/api/guild-events"

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load guild events"),
      )
    }

    const payload = (await response.json()) as {
      items: GuildEventRecord[]
      total: number
    }
    return payload
  },

  listTodayEvents: async (): Promise<GuildEventRecord[]> => {
    const response = await fetch("/api/guild-events/today", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load today's guild events"),
      )
    }

    const payload = (await response.json()) as { items: GuildEventRecord[] }
    return payload.items
  },

  getMemberEventDetail: async (
    eventId: string,
  ): Promise<GuildEventDetailRecord> => {
    const response = await fetch(`/api/guild-events/${eventId}`, {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load guild event details"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventDetailRecord }
    return payload.item
  },

  getAdminEventDetail: async (
    eventId: string,
  ): Promise<GuildEventDetailRecord> => {
    const response = await fetch(`/api/admin/guild-events/${eventId}`, {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load guild event details"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventDetailRecord }
    return payload.item
  },

  createEvent: async (input: EventCreateInput): Promise<GuildEventRecord> => {
    const response = await fetch("/api/admin/guild-events", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": getCsrfToken(),
      },
      body: JSON.stringify({
        gameKey: "where-winds-meet",
        ...input,
      }),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to create guild event"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventRecord }
    return payload.item
  },

  updateEvent: async (
    eventId: string,
    input: EventUpdateInput,
  ): Promise<GuildEventRecord> => {
    const response = await fetch(`/api/admin/guild-events/${eventId}`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": getCsrfToken(),
      },
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to update guild event"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventRecord }
    return payload.item
  },
  deleteEvent: async (eventId: string): Promise<void> => {
    const response = await fetch(`/api/admin/guild-events/${eventId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to delete guild event"),
      )
    }
  },

  cancelEvent: async (eventId: string): Promise<GuildEventRecord> => {
    const response = await fetch(`/api/admin/guild-events/${eventId}/cancel`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to cancel guild event"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventRecord }
    return payload.item
  },
  repostAnnouncement: async (eventId: string): Promise<GuildEventRecord> => {
    const response = await fetch(`/api/admin/guild-events/${eventId}/repost`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to repost event announcement"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventRecord }
    return payload.item
  },
  syncAnnouncementFromDb: async (
    eventId: string,
  ): Promise<GuildEventRecord> => {
    const response = await fetch(`/api/admin/guild-events/${eventId}/sync`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to sync event announcement"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventRecord }
    return payload.item
  },

  overrideSignup: async (
    eventId: string,
    memberId: string,
    input: SignupMutationInput,
  ): Promise<void> => {
    const response = await fetch(
      `/api/admin/guild-events/${eventId}/signups/${memberId}`,
      {
        method: "PUT",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": getCsrfToken(),
        },
        body: JSON.stringify(input),
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to update event RSVP"),
      )
    }
  },

  setMemberRsvp: async (
    eventId: string,
    input: SignupMutationInput,
  ): Promise<void> => {
    const response = await fetch(`/api/guild-events/${eventId}/rsvp`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": getCsrfToken(),
      },
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      const { code, message } = await readApiErrorBody(
        response,
        "Failed to update RSVP",
      )
      throw new GuildEventsApiError(message, code)
    }
  },

  duplicateEvent: async (
    eventId: string,
    input: EventDuplicateInput,
  ): Promise<GuildEventRecord> => {
    const response = await fetch(
      `/api/admin/guild-events/${eventId}/duplicate`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": getCsrfToken(),
        },
        body: JSON.stringify(input),
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to duplicate event"),
      )
    }

    const payload = (await response.json()) as { item: GuildEventRecord }
    return payload.item
  },

  listSchedules: async (): Promise<GuildEventSchedule[]> => {
    const response = await fetch("/api/admin/guild-events/schedules", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to load guild event schedules",
        ),
      )
    }

    const payload = (await response.json()) as { items: GuildEventSchedule[] }
    return payload.items
  },

  getScheduleOverview: async (): Promise<GuildEventScheduleOverview[]> => {
    const response = await fetch("/api/admin/guild-events/schedules/overview", {
      method: "GET",
      credentials: "include",
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to load guild event schedule overview",
        ),
      )
    }

    const payload = (await response.json()) as {
      items: GuildEventScheduleOverview[]
    }
    return payload.items
  },

  previewSchedulesToday: async (): Promise<
    GuildEventGenerationPreviewItem[]
  > => {
    const response = await fetch(
      "/api/admin/guild-events/schedules/generate-today/preview",
      {
        method: "POST",
        credentials: "include",
        headers: { "x-csrf-token": getCsrfToken() },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to preview scheduled guild events",
        ),
      )
    }

    const payload = (await response.json()) as {
      items: GuildEventGenerationPreviewItem[]
    }
    return payload.items
  },

  createSchedule: async (
    input: GuildEventScheduleCreateInput,
  ): Promise<GuildEventSchedule> => {
    const response = await fetch("/api/admin/guild-events/schedules", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": getCsrfToken(),
      },
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to create guild event schedule",
        ),
      )
    }

    const payload = (await response.json()) as { item: GuildEventSchedule }
    return payload.item
  },

  updateSchedule: async (
    scheduleId: string,
    input: GuildEventScheduleUpdateInput,
  ): Promise<GuildEventSchedule> => {
    const response = await fetch(
      `/api/admin/guild-events/schedules/${scheduleId}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": getCsrfToken(),
        },
        body: JSON.stringify(input),
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to update guild event schedule",
        ),
      )
    }

    const payload = (await response.json()) as { item: GuildEventSchedule }
    return payload.item
  },

  deleteSchedule: async (scheduleId: string): Promise<void> => {
    const response = await fetch(
      `/api/admin/guild-events/schedules/${scheduleId}`,
      {
        method: "DELETE",
        credentials: "include",
        headers: { "x-csrf-token": getCsrfToken() },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to delete guild event schedule",
        ),
      )
    }
  },

  generateSchedulesToday: async (): Promise<GuildEventGenerationResult> => {
    const response = await fetch(
      "/api/admin/guild-events/schedules/generate-today",
      {
        method: "POST",
        credentials: "include",
        headers: { "x-csrf-token": getCsrfToken() },
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to generate scheduled guild events",
        ),
      )
    }

    const payload = (await response.json()) as {
      result: GuildEventGenerationResult
    }
    return payload.result
  },
  parseStatus: assertStatus,
  parseRsvpState: assertState,
}
